import { c as _c } from "react/compiler-runtime";
import { ActionIcon, Button, ButtonProps, CopyButton, Indicator, Popover, Skeleton, Tooltip } from '@mantine/core';
import { useScrollIntoView } from '@mantine/hooks';
import { openConfirmModal } from '@mantine/modals';
import { StyleProps } from 'components';
import { useHover } from 'components/hooks/useHover';
import { usePortal } from 'components/portal/portal';
import { useState, useEffect, useCallback, useContext } from 'react';
import { MdCheckCircle, MdEdit, MdOutlineUndo, MdReply } from 'react-icons/md';
import { CommentItem, UseCommentPost } from './comments';
import { zCommentCfg } from './commentTypes';
import { CommentStatusIcon, statusIconProps, StatusType } from './CommentStatus';
import { Compose } from './Compose';
import { delay } from 'shared/promise';
import { splitBy, tree } from 'shared/collection';
import { notNull } from 'shared/type';
import { fmtDate } from 'shared/format';
import { uri } from 'shared/uri';
import { Expandable } from 'shared/ui';
import { HiCheck, HiLink, HiTrash } from 'react-icons/hi';
import { SAvatar, SAvatarGroup } from 'components/portal/SAvatar';
import { CardCollapsable } from 'components/Card';
import { MentionQuestion } from 'components/form/lexical/MentionNode';
import { GroupPanelContext, CommentItemChild, CommentsComponent } from './Comments';
import { DateTime } from 'luxon';
import { entries, uniqueBy, values } from 'remeda';
import { CommentReaction as R } from './CommentReaction';
import { twMerge } from 'tailwind-merge';
import { useQuestionUIItem } from 'components/question/useQuestions';
export const ShowMoreCommentsTitle = t0 => {
  const $ = _c(10);
  const {
    comments,
    noun
  } = t0;
  const t1 = noun ?? "comments";
  let t2;
  if ($[0] !== comments.length || $[1] !== t1) {
    t2 = <p className="text-sm">show {comments.length} older {t1}</p>;
    $[0] = comments.length;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== comments) {
    t3 = uniqueBy(tree(comments).all(), _temp).map(_temp2).filter(notNull);
    $[3] = comments;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t3) {
    t4 = <SAvatarGroup profiles={t3} />;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t2 || $[8] !== t4) {
    t5 = <div className="flex items-center gap-2">{t2}{t4}</div>;
    $[7] = t2;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
};
export const CommentSkeleton = t0 => {
  const $ = _c(6);
  const {
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = twMerge("ml-2 flex flex-col gap-2", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <CommentSkeletonLine />;
    t3 = <CommentSkeletonLine />;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t1) {
    t4 = <div className={t1}>{t2}{t3}</div>;
    $[4] = t1;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t4;
};
const CommentSkeletonLine = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="flex gap-2"><Skeleton height="1.8rem" width="1.8rem" /><Skeleton height="1.2em" className="mt-1" /></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
const {
  fromISO
} = DateTime;
export const maxDepth = 3;
export type CommentProps = {
  editable: boolean;
  comment: CommentItemChild;
  prev?: CommentItemChild;
  next?: CommentItemChild;
  onPost: (comment: UseCommentPost) => Promise<void>;
  deleteComment: (id: string) => void;
};
export const Comment = ({
  comment,
  editable,
  onPost: _onPost,
  deleteComment,
  className,
  prev,
  next
}: CommentProps & StyleProps) => {
  const {
    profile
  } = usePortal();
  const {
    displayMode,
    subquestions,
    query
  } = useContext(GroupPanelContext);
  const {
    id,
    parent_id,
    created_at,
    updated_at,
    children,
    deleted,
    depth,
    highlight: highlightRaw,
    isNew,
    question_id,
    subquestion_id
  } = comment;
  const c = comment.cfg;
  const uiCommon = {
    schema: zCommentCfg,
    subPath: [comment.id]
  };
  const uiEdit = useQuestionUIItem({
    ...uiCommon,
    type: 'edit-comment',
    initial: c,
    disabled: !editable
  });
  const uiReply = useQuestionUIItem({
    ...uiCommon,
    type: 'new-reply'
  });
  const [editing, setEditing] = useState(uiEdit.isDirtyVsInitial);
  const editDiff = created_at != null && updated_at != null ? fromISO(updated_at).diff(fromISO(created_at), 'seconds').seconds : undefined;
  const edited = (editDiff ?? 0) > 5;
  const {
    hovered: hoverReply,
    ref: replyRef
  } = useHover();
  const {
    hovered: hover,
    ref
  } = useHover();
  const {
    hovered: hoverChild,
    ref: childRef
  } = useHover();
  const {
    hovered: hoverMenu,
    ref: menuRef
  } = useHover();
  const {
    targetRef: scrollRef
  } = useScrollIntoView<HTMLDivElement>({});
  const [highlightState, setHighlight] = useState<boolean>(highlightRaw);
  useEffect(() => {
    if (highlightRaw) {
      setHighlight(true);
      setExpanded(true);
    } else delay(4000).then(() => setHighlight(false));
  }, [highlightRaw]);
  useEffect(() => {
    if (highlightRaw && scrollRef.current) {
      delay(800).then(() => {
        scrollRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      });
    }
  }, [highlightRaw, scrollRef]);
  const [expandedState, setExpanded] = useState<boolean>();
  const expanded = expandedState ?? (query?.comments_expand ? true : undefined);
  const [showReply, setShowReply] = useState<'show' | 'focus' | false>(uiReply.isDirtyVsInitial ? 'show' : false);
  const showInlineReply = showReply || comment.cfg.type != 'forecast-note' && depth == 0 && comment.children.length > 0;
  const showReplyInToolbar = !showInlineReply && depth + 1 < maxDepth;
  const [showAllReplies, setShowAllReplies] = useState(false);
  useEffect(() => {
    setShowAllReplies(query?.comments_expand ?? false);
  }, [query?.comments_expand]);
  const [repliesShown, repliesCollapsed] = splitBy(children.map(c_0 => ({
    comment: c_0,
    onPost: _onPost,
    deleteComment,
    editable: c_0.created_by == profile!.id
  }) satisfies CommentProps), (c_1, i) => children.length - i <= 6 || c_1.comment.isNew // show the more recent 3 or all new replies
  );
  const onPostEdit = useCallback(async (cfg: UseCommentPost['cfg']) => {
    await _onPost({
      cfg,
      id,
      parent_id
    });
    setEditing(false);
  }, [_onPost]);
  const {
    reactions
  } = R.useCommentReactions({
    comment_id: id
  });
  if (!profile || !comment) return <></>;
  const buttonProps = {
    variant: 'subtle',
    size: 'compact-sm'
  } as ButtonProps;
  const sameContext = (a: CommentItemChild, b: CommentItemChild) => a.question_id == b.question_id && a.subquestion_id == b.subquestion_id && a.created_by == b.created_by;
  const anyReaction = (reactions_0: R.DisplayReactions) => values(reactions_0).some(({
    count
  }) => count);
  const prevSame = prev != null && sameContext(prev, comment) && prev.children.length == 0;
  const nextSame = next != null && sameContext(next, comment) && comment.children.length == 0;
  const showMoreProps = {
    comment,
    created_at,
    edited,
    reactions
  };
  return <div className={`${className ?? ''} ${prevSame ? '' : 'pt-1'} ${nextSame ? '' : 'pb-1'}`}>
      <div className='flex'>
        <div className={`${highlightState || hoverReply || hoverChild || hover || hoverMenu ? 'bg-primary' : ''} mr-1 w-1 rounded ${highlightState ? '' : 'opacity-30'} ${parent_id != null ? '' : ''}`}></div>
        <div key={id} data-comment-id={id} ref={scrollRef} className={`flex w-full gap-2`}>
          <div>
            <Indicator zIndex={1} disabled={!isNew} title='new since you last opened this question' position={prevSame ? 'middle-center' : 'bottom-end'}>
              <div className='min-w-[24px] h-[24px]'>
                {!prevSame && <SAvatar profile={comment.profile} size='sm' />}
              </div>
            </Indicator>
          </div>
          <div ref={ref} className='w-full'>
            <Popover opened={editing ? false : hover} position='top-end' shadow='md' withinPortal={false} withArrow arrowPosition='center' arrowSize={10} offset={-8}>
              <Popover.Target>
                <div className={`w-full ${highlightState ? 'bg-primary/10' : ''}`}>
                  <div>
                    {deleted ? <span className='text-gray-400'>deleted</span> : <Expandable expanded={expanded} onChange={e => setExpanded(e)} maxHeight='300px' hideScroll disabled={editing} showMoreSection={nextSame && !anyReaction(reactions) ? undefined : <ShowMoreSection {...showMoreProps} />}>
                        {depth == 0 && displayMode == 'flat' && subquestion_id && !query?.subquestion && !prevSame && <MentionQuestion path={`${question_id}/${subquestion_id}`} title={subquestions[subquestion_id]?.title ?? ''} />}
                        <Compose stored={c} onPost={onPostEdit} onCancel={() => setEditing(false)} readonly={!editing} showStatus={parent_id == null} onBlur={({
                      isDirty
                    }) => {
                      if (!isDirty) setEditing(false);
                    }} ui={uiEdit} />
                      </Expandable>}
                  </div>
                </div>
              </Popover.Target>
              <Popover.Dropdown style={{
              padding: '0.1rem 0.2rem'
            }}>
                <div ref={menuRef} className='flex items-center gap-1'>
                  {entries(reactions).map(([t, r]) => <R.Button key={t} {...r} />)}
                  {showReplyInToolbar && <Button {...buttonProps} leftSection={<MdReply />} onClick={() => {
                  setShowReply('focus');
                }}>
                      reply
                    </Button>}
                  {c.status?.length && profile.hasRole('admin') ? <Button {...buttonProps} leftSection={c.resolved ? <MdOutlineUndo /> : <MdCheckCircle />} onClick={() => {
                  _onPost({
                    cfg: {
                      ...c,
                      resolved: !c.resolved
                    },
                    id,
                    parent_id
                  });
                }}>
                      {c.resolved ? 'Un-resolve' : 'Resolve'}
                    </Button> : null}
                  {editable && <>
                      <Button {...buttonProps} leftSection={<MdEdit />} onClick={() => {
                    setEditing(true);
                  }}>
                        edit
                      </Button>
                      <ActionIcon onClick={() => {
                    openConfirmModal({
                      centered: true,
                      title: 'Delete this comment?',
                      labels: {
                        confirm: 'Delete',
                        cancel: 'Cancel'
                      },
                      confirmProps: {
                        color: 'red',
                        variant: 'filled'
                      },
                      onConfirm: () => deleteComment(id)
                    });
                  }}>
                        <HiTrash />
                      </ActionIcon>
                    </>}
                  <CopyButton value={uri(window.location.href).mergeQuery({
                  comment_id: id
                }).url} timeout={2000}>
                    {({
                    copied,
                    copy
                  }) => <Tooltip label={copied ? 'Copied' : 'Copy link to comment'} withArrow>
                        <ActionIcon color={copied ? 'primary' : 'gray'} onClick={copy}>
                          {copied ? <HiCheck size='1rem' /> : <HiLink size='1rem' />}
                        </ActionIcon>
                      </Tooltip>}
                  </CopyButton>
                </div>
                {nextSame ? <div className='p-1'>
                    <ShowMoreSection {...showMoreProps} hideReactions />
                  </div> : <></>}
              </Popover.Dropdown>
            </Popover>
          </div>
        </div>
      </div>

      {(children.length > 0 || showInlineReply) && <div className='flex'>
          <div className={`${hoverReply || hoverChild ? 'bg-gray-200' : ''} w-1 shrink-0 rounded`}></div>
          <div className={`flex grow flex-col`}>
            {children.length > 0 && <div ref={childRef} className='pl-3 pt-2'>
                {repliesCollapsed.length > 0 && <CardCollapsable variant='text' opened={showAllReplies} onChange={setShowAllReplies} classNames={{
            title: 'text-sm ml-3 my-2',
            root: 'my-2'
          }} startCollapsed={repliesCollapsed.every(r_0 => !r_0.comment.highlight)} title={o => o ? <p className='py-1'>show less replies</p> : <ShowMoreCommentsTitle noun='replies' comments={repliesCollapsed.map(c_2 => c_2.comment)} />}>
                    <CommentsComponent comments={repliesCollapsed} />
                  </CardCollapsable>}
                <CommentsComponent comments={repliesShown} />
              </div>}
            {showInlineReply && <div ref={replyRef} className='my-1'>
                <Compose className='mb-0 pl-5 pt-1' onPost={async cfg_0 => {
            await _onPost({
              cfg: cfg_0,
              parent_id: id,
              subquestion_id: comment.subquestion_id
            });
            setShowReply('focus');
          }} onCancel={() => {
            setShowReply(false);
          }} onBlur={({
            isDirty: isDirty_0
          }) => {
            if (!isDirty_0) setShowReply(false);
          }} readonly={false} autoFocus={showReply == 'focus'} placeholder={`reply...`} showAvatar={false} ui={uiReply} />
              </div>}
          </div>
        </div>}
    </div>;
};
const ShowMoreSection = t0 => {
  const $ = _c(17);
  const {
    comment: t1,
    created_at,
    edited,
    reactions,
    hideReactions
  } = t0;
  const {
    cfg: c
  } = t1;
  let t2;
  if ($[0] !== c.resolved || $[1] !== c.status) {
    let t3;
    if ($[3] !== c.resolved) {
      t3 = s => <CommentStatusIcon key={s} {...statusIconProps(s as StatusType)} resolved={c.resolved} />;
      $[3] = c.resolved;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    t2 = c.status.map(t3);
    $[0] = c.resolved;
    $[1] = c.status;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[5] !== created_at) {
    t3 = created_at && <div className="whitespace-nowrap">{fmtDate(created_at, {
        showTime: true
      })}</div>;
    $[5] = created_at;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== edited) {
    t4 = edited && <div className="pl-2">edited</div>;
    $[7] = edited;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== hideReactions || $[10] !== reactions) {
    t5 = !hideReactions && entries(reactions).filter(_temp3).map(_temp4);
    $[9] = hideReactions;
    $[10] = reactions;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t2 || $[13] !== t3 || $[14] !== t4 || $[15] !== t5) {
    t6 = <div className="flex items-center gap-2 text-xs leading-3 text-gray-500">{t2}{t3}{t4}{t5}</div>;
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  return t6;
};
function _temp(c) {
  return c.created_by;
}
function _temp2(c_0) {
  return c_0.profile;
}
function _temp3(t0) {
  const [, t1] = t0;
  const {
    count
  } = t1;
  return count;
}
function _temp4(t0) {
  const [t, r] = t0;
  return <R.Button key={t} {...r} />;
}